import type {
  ListingStatus,
  OrderStatus,
  PaymentStatus,
  PayoutClawbackStatus,
  RefundStatus,
  RoyaltyReportStatus,
  SettlementStatus,
  SponsorshipApplicationStatus,
  SponsorshipStatus,
} from '@inflip/shared';
import { ORDER_STATUS_LABELS } from '@inflip/shared';

/** 상태 라벨 — 취소는 주체로 나눠 표기: SYSTEM(입금 기한 만료)=미입금 취소, USER/ADMIN=주문 취소 */
export function orderStatusLabel(
  status: OrderStatus,
  cancelledBy?: 'USER' | 'ADMIN' | 'SYSTEM' | null,
): string {
  if (status === 'CANCELLED' && cancelledBy) {
    return cancelledBy === 'SYSTEM' ? '미입금 취소' : '주문 취소';
  }
  return ORDER_STATUS_LABELS[status];
}

/**
 * 상태 표기 — 토스 스타일은 필 배지 대신 컬러 텍스트.
 * 진행 대기=앰버, 진행 중=블루, 완료=에메랄드, 문제=로즈/레드, 종결=그레이.
 */

export const ORDER_STATUS_TEXT: Record<OrderStatus, string> = {
  ORDER_CREATED: 'text-muted-foreground',
  PAYMENT_PENDING: 'text-amber-600',
  PAID: 'text-blue-600',
  LICENSE_ISSUED: 'text-emerald-600',
  SETTLEMENT_PENDING: 'text-amber-600',
  PAYOUT_COMPLETED: 'text-emerald-600',
  CANCELLED: 'text-muted-foreground',
  REFUNDED: 'text-rose-600',
  DISPUTED: 'text-red-600',
  TERMINATED: 'text-rose-600',
};

export const LISTING_STATUS_META: Record<ListingStatus, { label: string; cls: string }> = {
  DRAFT: { label: '작성 중', cls: 'text-muted-foreground' },
  PENDING_REVIEW: { label: '심사 대기', cls: 'text-amber-600' },
  PUBLISHED: { label: '판매 중', cls: 'text-emerald-600' },
  SUSPENDED: { label: '판매 중지', cls: 'text-rose-600' },
  ENDED: { label: '판매 종료', cls: 'text-muted-foreground' },
};

export const SETTLEMENT_STATUS_META: Record<SettlementStatus, { label: string; cls: string }> = {
  SCHEDULED: { label: '예정', cls: 'text-amber-600' },
  HOLDING: { label: '보류기간', cls: 'text-amber-600' },
  READY: { label: '지급 가능', cls: 'text-blue-600' },
  PAID_OUT: { label: '지급 완료', cls: 'text-emerald-600' },
  ON_HOLD: { label: '보류(수동)', cls: 'text-rose-600' },
  CANCELLED: { label: '취소', cls: 'text-muted-foreground' },
};

export const REFUND_STATUS_META: Record<RefundStatus, { label: string; cls: string }> = {
  REQUESTED: { label: '요청됨', cls: 'text-amber-600' },
  APPROVED: { label: '승인됨', cls: 'text-blue-600' },
  COMPLETED: { label: '지급 완료', cls: 'text-emerald-600' },
  REJECTED: { label: '거절됨', cls: 'text-muted-foreground' },
  CANCELLED: { label: '취소됨', cls: 'text-muted-foreground' },
};

/** 입금 관리 — 결제(주문 대금·추가금) 상태 */
export const PAYMENT_STATUS_META: Record<PaymentStatus, { label: string; cls: string }> = {
  PENDING: { label: '입금 대기', cls: 'text-amber-600' },
  CONFIRMED: { label: '입금 확인', cls: 'text-emerald-600' },
  FAILED: { label: '실패', cls: 'text-rose-600' },
  EXPIRED: { label: '기한 만료', cls: 'text-muted-foreground' },
  CANCELLED: { label: '취소', cls: 'text-muted-foreground' },
};

/** 정산 지급 뒤 환불의 판매자 회수 */
export const PAYOUT_CLAWBACK_STATUS_META: Record<
  PayoutClawbackStatus,
  { label: string; cls: string }
> = {
  PENDING: { label: '판매자 회수 대기', cls: 'text-amber-600' },
  OVERDUE: { label: '회수 기한 경과', cls: 'text-rose-600' },
  COLLECTED: { label: '판매자 회수 완료', cls: 'text-emerald-600' },
  CANCELLED: { label: '회수 취소', cls: 'text-muted-foreground' },
};

export const ROYALTY_STATUS_META: Record<RoyaltyReportStatus, { label: string; cls: string }> = {
  REPORTED: { label: '입금 확인 대기', cls: 'text-amber-600' },
  CONFIRMED: { label: '지급 대기', cls: 'text-blue-600' },
  PAID_OUT: { label: '정산 완료', cls: 'text-emerald-600' },
  VOIDED: { label: '반려됨', cls: 'text-muted-foreground' },
};

export const SPONSORSHIP_STATUS_META: Record<SponsorshipStatus, { label: string; cls: string }> = {
  PENDING: { label: '심사 대기', cls: 'text-amber-600' },
  OPEN: { label: '모집 중', cls: 'text-blue-600' },
  REJECTED: { label: '반려됨', cls: 'text-rose-600' },
  CLOSED: { label: '모집 마감', cls: 'text-muted-foreground' },
  CANCELLED: { label: '요청 취소', cls: 'text-muted-foreground' },
};

/** 협찬 지원 상태 — 사용자 화면 라벨(SPONSORSHIP_APPLICATION_STATUS_LABELS)과 같은 말 */
export const SPONSORSHIP_APPLICATION_STATUS_META: Record<
  SponsorshipApplicationStatus,
  { label: string; cls: string }
> = {
  PENDING: { label: '승인 대기', cls: 'text-amber-600' },
  APPROVED: { label: '확정', cls: 'text-blue-600' },
  REJECTED: { label: '거절됨', cls: 'text-rose-600' },
  CANCELLED: { label: '지원 취소', cls: 'text-muted-foreground' },
  EXPIRED: { label: '자동 취소', cls: 'text-muted-foreground' },
  COMPLETED: { label: '미션 완료', cls: 'text-emerald-600' },
};
